import { useState, useEffect } from 'react';
import { useSelectExtractAssetsStore } from '../../../services/selectExtractAssetsStore';
import { config } from '../../../config';
import { prepareHeaders } from '../../../configuration/tokenHandling/prepareHeaders';
import TableViewToggles, { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { IInfraction } from '../models/Infraction';
import { IInfractionItem } from '../models/InfractionItem';
import { ITrip } from '../models/Trip';
import { IInfractionResult } from '../models/InfractionResult';
import { useDateFilterStore } from '../../../services/dateFilterStore';


type UseInfractionsDataProps = {
  externalViewType: TableViewTogglesViewType;
  hasData: boolean;
  infractionName: string;
};

export const useInfractionsData = ({
  externalViewType,
  hasData,
  infractionName
}: UseInfractionsDataProps) => {
  const { selectedExtractAssets } = useSelectExtractAssetsStore();
  const { startDate, endDate } = useDateFilterStore();
  const [infractionsList, setInfractionsList] = useState<IInfractionResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [viewType, setViewType] = useState(externalViewType || TableViewToggles.VIEW_TYPE_TABLE);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  const handleViewTypeChange = (newViewType: TableViewTogglesViewType) => setViewType(newViewType);

  const handleToggleRow = (id: string) => {
    setExpandedRows(prev => (prev.includes(id) ? prev.filter(expanded => expanded !== id) : [...prev, id]));
  };

  useEffect(() => {
    if (!hasData) {
      setIsLoading(true);
      setTimeout(() => setIsLoading(false), 1000);
      setInfractionsList([]);
      return;
    }

    const getAssets = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const headers = await prepareHeaders(new Headers());
        const response = await fetch(config.backend.SCORES_SERVICE + '/v2/scores/infractions', {
          method: 'POST',
          headers,
          body: JSON.stringify({ asset_id: selectedExtractAssets[0], infraction_target: infractionName, start_date: startDate, end_date: endDate }),
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const jsonData = (await response.json()) as IInfraction;
        const trips = jsonData.items as ITrip[];

        setInfractionsList(trips.map((item: ITrip) => {

          const measuredValue = item.items.reduce((prev: IInfractionItem, current: IInfractionItem) => {
            return prev.measured > current.measured ? prev : current;
          }).measured;

          return {
            tripStart: item.trip_start,
            tripFinal: item.trip_final,
            cumKilometers: item.cum_kilometers,
            engineHours: item.engineHours,
            occurrences: item.events,
            configKilometers: item.config_kilometers,
            measuredValue: measuredValue,
            eventPerKilometer: item.epk,
            items: item.items.map((item: IInfractionItem) => {
              return {
                eventLocation: '',
                detailLastUpdate: item.lastUpdate,
                lat: item.latitude,
                lng: item.longitude,
                detailEngineHours: '',
                detailCumKilometers: '',
                detailConfiguredKilometers: item.configuration ? item.configuration : 0,
                detailMeasuredValue: item.measured,
                detailCauses: item.details
              }
            })
          }
        }));
      } catch (e: any) {
        console.error('Error fetching infractions:', e);
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (hasData) {
      getAssets();
    }
  }, [selectedExtractAssets, startDate, endDate]);

  return {
    infractionsList,
    isLoading,
    error,
    searchValue,
    setSearchValue,
    handleViewTypeChange,
    viewType,
    handleToggleRow,
    expandedRows,
  };
};